import { z } from 'zod';

/**
 * Registry of every feature flag the platform evaluates through OpenFeature.
 *
 * The zod enum is the single source of truth for flag keys: compile-time
 * safety comes from the inferred `FeatureFlagKey` union, runtime safety from
 * parsing untrusted input (e.g. tRPC procedure input) with the schema.
 *
 * Keys must match the flag key configured in every provider backend (Datadog
 * Feature Flags, the local flagd definition file in `api-stack/flagd/`, and
 * any in-memory test configuration).
 */
export const featureFlagKeySchema = z.enum([
  'easter_eggs',
  'object_toolbar',
  'indicator_results_chart_enhancements',
  'indicator_widget_tolerances',
  'appetite_links',
  'attach_file_custom_field',
  'attestation_improvements',
  'conditional_fields',
  'custom_attribute_alternate_values',
  'day_attestations',
  'empty_default_dashboard',
  'form_sections',
  'issue-allica',
  'issue-gc',
  'policy_auto_draft',
  'scoring_settings_data',
  'trpc',
  'statuses',
  'statuses_risk',
  'statuses_control',
  'statuses_operational',
  'statuses_compliance',
  'statuses_platform_surfaces',
  'risk_score_history',
  'notification_test_send',
  'g2_review_prompt',
  'smart_copy_custom_datasource',
  'oversight_rating_performance_entry',
  'ai_agent_form_prefill',
  'ai_agent_flag_pipeline_pilot',
  'ai_agent_draft_prompt',
  'flag_evaluator_probe',
]);

export type FeatureFlagKey = z.infer<typeof featureFlagKeySchema>;

export interface FeatureFlagDefinition {
  /**
   * Value used when the provider is unreachable, the flag is missing from the
   * backend, or evaluation fails. Defaults are deliberately the "feature off"
   * value so a flagging outage never enables unfinished behaviour.
   */
  defaultValue: boolean;
  description: string;
}

export const featureFlagDefinitions: Record<
  FeatureFlagKey,
  FeatureFlagDefinition
> = {
  easter_eggs: {
    defaultValue: false,
    description:
      'Gates hidden easter-egg UI — currently the drag "bounciness" slider on the customisable ribbon filter editor.',
  },
  object_toolbar: {
    defaultValue: false,
    description:
      'Gates the object-level toolbar (CUS-27) that relocates per-object actions (export/delete) plus Save/Cancel into a single header bar. Piloted on the Indicator detail page (CUS-135) before fanning out to the other registers.',
  },
  indicator_results_chart_enhancements: {
    defaultValue: false,
    description:
      'Gates Deliverable 1 of the indicator chart enhancements — the results-tab Actions dropdown, date range picker, chart x-axis/data changes, and PNG export.',
  },
  indicator_widget_tolerances: {
    defaultValue: false,
    description:
      'Gates Deliverable 2 of the indicator chart enhancements — the tolerances/appetites toggle on the dashboard line chart widget.',
  },
  appetite_links: {
    defaultValue: false,
    description: 'Gates linking appetites to other records in the linked-items UI.',
  },
  attach_file_custom_field: {
    defaultValue: false,
    description: 'Gates the Attach File custom field type (CUS-75).',
  },
  attestation_improvements: {
    defaultValue: false,
    description: 'Gates in-progress attestation workflow improvements.',
  },
  conditional_fields: {
    defaultValue: false,
    description: 'Gates conditional form-field logic (show/hide fields by rule).',
  },
  custom_attribute_alternate_values: {
    defaultValue: false,
    description: 'Gates alternate-value support on custom attributes.',
  },
  day_attestations: {
    defaultValue: false,
    description: 'Legacy Meta.features flag — gates day-based attestation scheduling.',
  },
  empty_default_dashboard: {
    defaultValue: false,
    description: 'Gates rendering an empty default dashboard for new orgs.',
  },
  form_sections: {
    defaultValue: false,
    description: 'Gates sectioned form rendering (grouped form fields).',
  },
  'issue-allica': {
    defaultValue: false,
    description: 'Gates the Allica-specific issue variant.',
  },
  'issue-gc': {
    defaultValue: false,
    description: 'Gates the GC-specific issue variants.',
  },
  policy_auto_draft: {
    defaultValue: false,
    description: 'Gates automatic policy draft generation.',
  },
  scoring_settings_data: {
    defaultValue: false,
    description: 'Gates the scoring-settings data configuration surface.',
  },
  trpc: {
    defaultValue: false,
    description:
      'Data-transport switch (ENG-399 Phase 2, ~331 refs). When on, migrated reads/mutations resolve via tRPC v3 instead of GraphQL/Apollo — the per-org toggle behind the createQueryHook / RBAC hook-factory branch on both web and the tRPC backend. Off = the legacy GraphQL path (the safe pre-migration default). This ticket (ENG-408) only registers the key + Datadog targeting; the readers move off Meta.features onto OpenFeature in ENG-540.',
  },
  statuses: {
    defaultValue: false,
    description:
      'Top-level kill switch (M1) for the unified lifecycle-status rollout. Every per-domain `statuses_*` flag is gated behind this: a domain surface shows lifecycle status only when both `statuses` and its domain flag are on. Migrated from `Meta.features` to OpenFeature/Datadog targeting under ENG-409 (ENG-399 Phase 2).',
  },
  statuses_risk: {
    defaultValue: false,
    description:
      'Gates the unified lifecycle-status UI for the Risk domain (M2): Risks and Risk Ratings. Effective only when `statuses` is also on.',
  },
  statuses_control: {
    defaultValue: false,
    description:
      'Gates the unified lifecycle-status UI for the Control domain (M3): Controls and Control Tests. Effective only when `statuses` is also on.',
  },
  statuses_operational: {
    defaultValue: false,
    description:
      'Gates the unified lifecycle-status UI for the Operational domain (M4): Actions, Issues, Indicators, Appetites, Third Parties, Policies, and Policy Ratings. Effective only when `statuses` is also on.',
  },
  statuses_compliance: {
    defaultValue: false,
    description:
      'Gates the unified lifecycle-status UI for the Compliance domain (M5): Compliance Obligations, Ratings, Assessments, Activities, Internal Audits, and Findings. Effective only when `statuses` is also on.',
  },
  statuses_platform_surfaces: {
    defaultValue: false,
    description:
      'Gates the unified lifecycle-status UI on cross-cutting platform surfaces (M6): registers, dashboards, My Items, pickers, and the customisable data source. Effective only when `statuses` is also on.',
  },
  risk_score_history: {
    defaultValue: false,
    description:
      'Gates the risk Score History tab (RSP-6566) — the per-risk residual/inherent score trend chart and audit-history table.',
  },
  notification_test_send: {
    defaultValue: false,
    description:
      'Gates the notification test-send tooling: the `notifications.test.send` tRPC mutation and its settings-modal UI, which trigger every feed workflow with fixture data to the calling user for template/renderer QA. Non-production only.',
  },
  g2_review_prompt: {
    defaultValue: false,
    description:
      'Gates the G2 in-app review prompt (RSP-6610) — the login-time modal embedding the G2 review iframe for RiskManager/StandardEnhanced users, and its backing `g2Review` tRPC procedures. Rolled out per-org via orgId targeting.',
  },
  smart_copy_custom_datasource: {
    defaultValue: false,
    description:
      'Gates the Custom Data Source Smart Copy action (WOR-116) — the register Actions-dropdown "Copy" option plus single-select tickboxes that pre-fill a new custom data source from an existing one (prefill lands in WOR-117). Read via `useIsFeatureFlagEnabled` on the Custom Data Sources register, which resolves from OpenFeature (flagd/Datadog) or the seeded Meta.features override for e2e test orgs (ENG-540).',
  },
  oversight_rating_performance_entry: {
    defaultValue: false,
    description:
      'Gates the oversight-rating entry point on the control/risk performance screens (RSP-6673). Migrated from a per-org `Meta.features` flag to OpenFeature/Datadog targeting under ENG-586 (ENG-399 Phase 2); unlike the two `smart_copy_*` flags it was never in the OpenFeature registry before. This ticket only registers the key + Datadog targeting; the reader (`useIsFeatureFlagEnabled`) stays on Meta.features until a later reader swap.',
  },
  ai_agent_form_prefill: {
    defaultValue: false,
    description:
      "Gates suggest-and-confirm AI form prefill (AID-240): the active-form publisher, the pageContext.activeForm block it produces, and the chat panel's ProposalCard. Read through `useIsFormPrefillEnabled`, which layers this on the `ai.subModules.agent` entitlement AND an internal-user check — cut 1 is internal-only, piloted on the Action form. The agent side is gated separately by FORM_PREFILL_ENABLED on the ai-agent runtime, so the tool is not registered at all where this is off. Nothing here grants write authority: applying a proposal is a local setValue and the user still saves through perm_v2.",
  },
  ai_agent_flag_pipeline_pilot: {
    defaultValue: false,
    description:
      'Plumbing-validation flag for the AI-agent feature-flag pipeline (AID-325). The ai-gateway evaluates it per turn (targetingKey = orgId) and forwards it to the ai-agent in the POST /invocations body; the agent threads it through run-turn with a safe-off default. Gates NO product behaviour — it exists to prove the gateway→agent forwarding path end-to-end (and to be togglable in the Datadog UI / flagd-ui without a deploy) before the first real ai_agent_* capability flag lands.',
  },
  ai_agent_draft_prompt: {
    defaultValue: false,
    description:
      "Gates the chat panel's draft-prompt bar (AID-290) — the internal-only toggle that asks the agent to resolve the Arize `draft` prompt label instead of the published one. Layered ON TOP of the internal-user check in `AIChatSidePanel`, not a replacement for it. It exists because the bar is pure frontend and had no way to know whether the runtime behind it can honour the toggle: the agent side is gated separately by `ARIZE_PROMPT_ENABLED` (per-env, `aiAgentArizePromptEnabled` in cdk-stack/lib/envSettings), and where that is off the flag is discarded and every turn uses the base GRC prompt — leaving a control that claimed \"Responses use unpublished changes\" while changing nothing. Target it per environment via the `env` dimension so it tracks that CDK gate: OFF for dev-cloud (prompt hub disabled there), ON for staging/prod. Same two-gate shape as `ai_agent_form_prefill`.",
  },
  flag_evaluator_probe: {
    defaultValue: false,
    description:
      "Synthetic key evaluated ONLY by the scheduled flag-evaluator probe (ENG-674, cdk-stack/lambda/flag-evaluator-probe). Gates NO product behaviour and nothing reads its value — it exists so the probe's traffic is separable from real reader traffic in the evaluator's per-decision logs, which log `flagKey` on every evaluation. ENG-671's planned \"absence of evaluations\" monitor filters this key out; without a dedicated key the probe would evaluate a real product flag every few minutes and mask exactly the silence that monitor watches for. In principle it needs no Datadog targeting — an unconfigured key answers FLAG_NOT_FOUND, which still proves the whole chain round-tripped and which the probe treats as healthy, the same split the evaluator itself applies (services/feature-flag-evaluator/src/server.ts). It is configured in Datadog anyway, serving false to everyone with no targeting rule, because dd-trace's answer for a wholly unconfigured key is unconfirmed and a GENERAL answer would latch the degraded alarm from the first scheduled run — see docs/observability/flag-evaluator-probe-alarms.md. Carried in api-stack/flagd/flags.flagd.json as well, defaulting off, per the both-backends rule in packages/feature-flags/CLAUDE.md. Nothing in a flagd-backed stage evaluates it — the probe only runs where the remote evaluator is enabled, all Datadog-backed — so the entry is inert by design rather than load-bearing; it is there so the registry and the flagd file stay in step and a reader does not have to work out whether the omission was deliberate.",
  },
};

export const featureFlagKeys: FeatureFlagKey[] = featureFlagKeySchema.options;
